import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { Search, X } from "lucide-react"
import { getYears, SUBJECTS } from "@/lib/utils"

const years = getYears()
const subjects = SUBJECTS

interface PaperFiltersProps {
  searchQuery: string
  selectedYear: string
  selectedSubject: string
  onSearchChange: (value: string) => void
  onYearChange: (value: string) => void
  onSubjectChange: (value: string) => void
  onClearFilters: () => void
}

export function PaperFilters({
  searchQuery,
  selectedYear,
  selectedSubject,
  onSearchChange,
  onYearChange,
  onSubjectChange,
  onClearFilters,
}: PaperFiltersProps) {
  const hasFilters = selectedYear || selectedSubject || searchQuery

  const activeFilters = [
    selectedYear && { label: `${selectedYear}年`, value: "year" },
    selectedSubject && { label: selectedSubject, value: "subject" },
  ].filter(Boolean) as { label: string; value: string }[]

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Search and Dropdowns */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="搜索试卷标题或地区"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex sm:gap-3">
          <Select value={selectedYear} onValueChange={onYearChange}>
            <SelectTrigger className="w-full sm:w-32">
              <SelectValue placeholder="年份" />
            </SelectTrigger>
            <SelectContent>
              {years.map((year) => (
                <SelectItem key={year} value={year.toString()}>
                  {year}年
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedSubject} onValueChange={onSubjectChange}>
            <SelectTrigger className="w-full sm:w-32">
              <SelectValue placeholder="科目" />
            </SelectTrigger>
            <SelectContent>
              {subjects.map((subject) => (
                <SelectItem key={subject} value={subject}>
                  {subject}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {hasFilters && (
          <Button variant="ghost" onClick={onClearFilters} className="hidden gap-2 sm:flex">
            清除筛选
          </Button>
        )}
      </div>

      {/* Active Filters Badges */}
      {activeFilters.length > 0 && (
        <div className="flex items-center gap-2">
          {activeFilters.map((filter) => (
            <Badge key={filter.value} variant="secondary" className="gap-1 pr-1">
              {filter.label}
              <button
                onClick={() => {
                  if (filter.value === "year") onYearChange("")
                  if (filter.value === "subject") onSubjectChange("")
                }}
                className="rounded-full p-0.5 hover:bg-muted"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          <Button variant="ghost" size="sm" onClick={onClearFilters}>
            清除全部
          </Button>
        </div>
      )}

      {/* Mobile Clear Button */}
      {hasFilters && (
        <Button variant="ghost" size="sm" onClick={onClearFilters} className="w-full sm:hidden">
          清除筛选
        </Button>
      )}
    </div>
  )
}
